import { EnumLeverageProductsIndication } from "src/api/client/extended-search.api";
import { IKeyFigure, ITags } from "src/api/server/extended-search";
import { EnumSortDirection } from "src/components/pages/search/components/sort-direction-button/sort-direction-button";
import { IKeyFigureValue, ISelectedRanking, ITagValue } from "src/components/pages/search/components/types";
import { SAVINGS_PLAN_TAG, TAG_IDS_IS_INVESTABLE } from "src/components/pages/search/constants";
import { ISearch, ISearchRequestParamsOmitCountry } from "src/components/pages/search/search.types";
import { ISearchStore, searchStore } from "src/components/pages/search/store/search-store";
import {
    getMappedStore,
    getShouldSetIsInvestable,
    isKeyFigureValueDefault,
    getNewSelectedTags,
    isPopularSavingsPlanTagSelected,
} from "src/components/pages/search/store/utils";
import { ICountry } from "src/consts/locale";
import { ISortDirectionType } from "src/types/common";
import { EnumCountryKey } from "src/types/common-enum";

type IDefaultPropsOnReset = {
    sortDirection: ISortDirectionType;
    selectedRanking: ISelectedRanking;
    country: string;
};

const setKeyFigures = (newKeyFigures: Array<IKeyFigureValue>) => {
    searchStore.keyFigures = newKeyFigures;
};

const setTags = (newTagIds: Array<ITagValue>) => {
    searchStore.tags = newTagIds;
};

const setIsInvestable = (value: boolean) => {
    searchStore.isInvestable = value;
};

const setIsAtInvestable = (value: boolean) => {
    searchStore.isAtInvestable = value;
};

const setIsChInvestable = (value: boolean) => {
    searchStore.isChInvestable = value;
};

export const setQuery = (value: string) => {
    searchStore.query = value;
};

const setSortDirection = (value: ISortDirectionType) => {
    searchStore.sortDirection = value;
};

const setSelectedRanking = (value: ISelectedRanking) => {
    searchStore.selectedRanking = value;
};

const setBrokersIds = (newBrokersIds: Array<string>) => {
    searchStore.brokersIds = newBrokersIds;
};

const setLeverageProductsIndication = (leverageProductsIndication: EnumLeverageProductsIndication) => {
    searchStore.leverageProductsIndication = leverageProductsIndication;
};

const resetStore = ({ selectedRanking, sortDirection, country }: IDefaultPropsOnReset) => {
    searchStore.query = "";
    searchStore.selectedRanking = selectedRanking;
    searchStore.sortDirection = sortDirection;
    searchStore.keyFigures = [];
    searchStore.tags = [];
    searchStore.brokersIds = [];
    searchStore.isInvestable = country !== EnumCountryKey.INT;
    searchStore.isAtInvestable = country === EnumCountryKey.AT;
    searchStore.isChInvestable = country === EnumCountryKey.CH;
    searchStore.leverageProductsIndication = EnumLeverageProductsIndication.AllWikifolios;
};

interface IHandleTags extends ISearch {
    isChecked: boolean;
    value: string;
    isPopular: boolean;
    tagsData: ITags;
}

export const handleTags = ({ isChecked, value, isPopular, tagsData, onSearch }: IHandleTags) => {
    const shouldResetBrokersIds = SAVINGS_PLAN_TAG === value && isChecked === false;

    const newSelectedTags = getNewSelectedTags(isChecked, value, isPopular, tagsData, searchStore.tags);
    const shouldIsInvestableBeSet = getShouldSetIsInvestable({ newSelectedTags, isInvestable: searchStore.isInvestable });
    shouldIsInvestableBeSet && setIsInvestable(true);
    shouldResetBrokersIds && setBrokersIds([]);

    setTags(newSelectedTags);

    onSearch(getMappedStore(searchStore));
};

export const handleResetTags = (onSearch: (params: ISearchRequestParamsOmitCountry) => void) => {
    if (searchStore.tags.length) {
        setTags([]);
        setBrokersIds([]);

        onSearch(getMappedStore(searchStore));
    }
};

interface IHandleKeyFigureChange extends ISearch {
    newKeyFigureValue: IKeyFigureValue;
    keyFiguresData: ReadonlyArray<IKeyFigure>;
}

export const handleKeyFigureChange = ({ newKeyFigureValue, onSearch, keyFiguresData }: IHandleKeyFigureChange) => {
    const valueOfNewKeyFigureInStore = searchStore.keyFigures.find(keyFigure => keyFigure.id === newKeyFigureValue.id);

    if (isKeyFigureValueDefault(newKeyFigureValue, keyFiguresData) && valueOfNewKeyFigureInStore === undefined) {
        return;
    }

    if (isKeyFigureValueDefault(newKeyFigureValue, keyFiguresData)) {
        const newKeyFigures = [...searchStore.keyFigures].filter(keyFigure => keyFigure.id !== newKeyFigureValue.id);

        setKeyFigures(newKeyFigures);
        onSearch(getMappedStore(searchStore));
    } else {
        const keyFigureDefaultValue = keyFiguresData.find(keyFigureValue => keyFigureValue.id === newKeyFigureValue.id);
        if (keyFigureDefaultValue) {
            const defaultKeyFigureMinimum = keyFigureDefaultValue?.steps[0];
            const defaultKeyFigureMaximum = keyFigureDefaultValue?.steps.at(-1);
            newKeyFigureValue.min = newKeyFigureValue.min !== defaultKeyFigureMinimum ? newKeyFigureValue.min : undefined;
            newKeyFigureValue.max = newKeyFigureValue.max !== defaultKeyFigureMaximum ? newKeyFigureValue.max : undefined;
        }

        const doesNewKeyFigureValueExistInStore = valueOfNewKeyFigureInStore !== undefined;
        if (doesNewKeyFigureValueExistInStore) {
            const isNewValueSameAsInStore =
                newKeyFigureValue.max === valueOfNewKeyFigureInStore.max && newKeyFigureValue.min === valueOfNewKeyFigureInStore.min;
            if (!isNewValueSameAsInStore) {
                const newKeyFigures = [...searchStore.keyFigures];
                const indexOfNewKeyFigure = newKeyFigures.findIndex(keyFigure => keyFigure.id === newKeyFigureValue.id);
                newKeyFigures[indexOfNewKeyFigure] = newKeyFigureValue;

                setKeyFigures(newKeyFigures);
                onSearch(getMappedStore(searchStore));
            }
        } else {
            setKeyFigures([...searchStore.keyFigures, newKeyFigureValue]);
            onSearch(getMappedStore(searchStore));
        }
    }
};

export const handleResetKeyFigures = (onSearch: (params: ISearchRequestParamsOmitCountry) => void) => {
    setKeyFigures([]);
    onSearch(getMappedStore(searchStore));
};

interface IHandleInvestable extends ISearch {
    isChecked: boolean;
}
export const handleIsInvestable = ({ isChecked, onSearch }: IHandleInvestable) => {
    const newTags = searchStore.tags.filter(tag => TAG_IDS_IS_INVESTABLE.every(tagId => tagId !== tag.value));

    if (!isChecked) {
        setTags(newTags);
        setBrokersIds([]);
        setIsAtInvestable(false);
        setIsChInvestable(false);
    }

    setIsInvestable(isChecked);

    onSearch(getMappedStore(searchStore));
};

export const handleIsInvestableInCountry = ({ isChecked, country, onSearch }: IHandleInvestable & { country: ICountry }) => {
    switch (country) {
        case EnumCountryKey.AT:
            if (isChecked) {
                setIsInvestable(true);
            }
            setIsAtInvestable(isChecked);
            onSearch(getMappedStore(searchStore));
            break;

        case EnumCountryKey.CH:
            setIsChInvestable(isChecked);

            if (isChecked) {
                setIsInvestable(true);
            }
            onSearch(getMappedStore(searchStore));
            break;
    }
};

interface IHandleBrokersIds extends ISearch {
    isChecked: boolean;
    brokerId: string;
}
export const handleBrokersIds = ({ isChecked, brokerId, onSearch }: IHandleBrokersIds) => {
    const selectedBrokersIds = isChecked ? [...searchStore.brokersIds, brokerId] : searchStore.brokersIds.filter(id => id !== brokerId);
    setBrokersIds(selectedBrokersIds);

    onSearch(getMappedStore(searchStore));
};

interface IHandleResetSearch extends ISearch {
    sortDirection: ISortDirectionType;
    selectedRanking: ISelectedRanking;
    country: string;
}

export const handleResetSearch = ({ sortDirection, selectedRanking, country, onSearch }: IHandleResetSearch) => {
    resetStore({ sortDirection, selectedRanking, country });
    onSearch(getMappedStore(searchStore));
};

interface IHandleSearchInputField extends ISearch {
    relevanceRanking: ISelectedRanking;
}
export const handleSearchInputField = ({ relevanceRanking, onSearch }: IHandleSearchInputField) => {
    setSortDirection(relevanceRanking.sortDirection);
    setSelectedRanking(relevanceRanking);

    onSearch(getMappedStore(searchStore));
};

export const handleSortDirection = ({ onSearch }: ISearch) => {
    const sortDirection =
        searchStore.sortDirection === EnumSortDirection.PointingDown ? EnumSortDirection.PointingUp : EnumSortDirection.PointingDown;
    setSortDirection(sortDirection);

    onSearch(getMappedStore(searchStore));
};

interface ISortingChange extends ISearch {
    sortByRankingId: string | null;
    sortDirection: ISortDirectionType;
}
export const handleSortingChange = ({ sortByRankingId, sortDirection, onSearch }: ISortingChange) => {
    if (sortByRankingId === searchStore.selectedRanking?.id) {
        return;
    }

    setSortDirection(sortByRankingId === null ? null : sortDirection);
    setSelectedRanking({ id: sortByRankingId, sortDirection });

    onSearch(getMappedStore(searchStore));
};
interface IHandleLeverageProducts extends ISearch {
    leverageProductsIndication: EnumLeverageProductsIndication;
}
export const handleLeverageProducts = ({ leverageProductsIndication, onSearch }: IHandleLeverageProducts) => {
    setLeverageProductsIndication(leverageProductsIndication);
    onSearch(getMappedStore(searchStore));
};

export const handleResetPopularFilters = ({ onSearch }: ISearch) => {
    if (isPopularSavingsPlanTagSelected(searchStore.tags)) {
        setBrokersIds([]);
        setIsInvestable(true);
    }

    setKeyFigures(searchStore.keyFigures.filter(keyFigure => !keyFigure.isPopular));
    setTags(searchStore.tags.filter(tag => !tag.isPopular));
    setLeverageProductsIndication(EnumLeverageProductsIndication.AllWikifolios);

    onSearch(getMappedStore(searchStore));
};

export const handleSearch = ({ onSearch }: ISearch) => {
    onSearch(getMappedStore(searchStore));
};

export const setDefaultFilters = (ranking: ISelectedRanking, country: string) => {
    setSelectedRanking(ranking);
    setSortDirection(ranking.sortDirection);
    setLeverageProductsIndication(EnumLeverageProductsIndication.AllWikifolios);
    setIsInvestable(country === EnumCountryKey.INT ? false : true);
    switch (country) {
        case EnumCountryKey.AT:
            setIsChInvestable(false);
            break;
        case EnumCountryKey.CH:
            setIsAtInvestable(false);
            break;
        default:
            setIsChInvestable(false);
            setIsAtInvestable(false);
            break;
    }
};

export const setNewSearchStoreValues = (newSearchStore: ISearchStore) => {
    Object.assign(searchStore, newSearchStore);
};
